// SQLite built-in function names emitted by `sqliteResolver` when the
// cursor is at a position that accepts a function call.
//
// Selection criteria: functions documented in the SQLite docs "Built-In
// Scalar / Aggregate / Window / Date-Time / JSON Functions" pages that
// surface in normal user authoring. Newer functions gated by version
// (`unixepoch`, `format`, `iif`, JSONB ops, RETURNING-only helpers) are
// included unconditionally — the version-gating pipeline owns the badge /
// demote pass; the curated list itself is the union across supported
// SQLite versions.

const SQLITE_BUILTIN_FUNCTION_LIST: readonly string[] = [
  // Aggregate
  'AVG', 'COUNT', 'GROUP_CONCAT', 'STRING_AGG', 'MAX', 'MIN', 'SUM',
  'TOTAL',
  // Core scalar
  'ABS', 'CHANGES', 'CHAR', 'COALESCE', 'CONCAT', 'CONCAT_WS', 'FORMAT',
  'GLOB', 'HEX', 'IFNULL', 'IIF', 'INSTR', 'LAST_INSERT_ROWID', 'LENGTH',
  'LIKE', 'LIKELIHOOD', 'LIKELY', 'LOAD_EXTENSION', 'LOWER', 'LTRIM',
  'NULLIF', 'OCTET_LENGTH', 'PRINTF', 'QUOTE', 'RANDOM', 'RANDOMBLOB',
  'REPLACE', 'ROUND', 'RTRIM', 'SIGN', 'SOUNDEX', 'SQLITE_COMPILEOPTION_GET',
  'SQLITE_COMPILEOPTION_USED', 'SQLITE_OFFSET', 'SQLITE_SOURCE_ID',
  'SQLITE_VERSION', 'SUBSTR', 'SUBSTRING', 'TOTAL_CHANGES', 'TRIM',
  'TYPEOF', 'UNHEX', 'UNICODE', 'UNLIKELY', 'UPPER', 'ZEROBLOB',
  // Math (3.35+)
  'ACOS', 'ACOSH', 'ASIN', 'ASINH', 'ATAN', 'ATAN2', 'ATANH', 'CEIL',
  'CEILING', 'COS', 'COSH', 'DEGREES', 'EXP', 'FLOOR', 'LN', 'LOG',
  'LOG2', 'LOG10', 'MOD', 'PI', 'POW', 'POWER', 'RADIANS', 'SIN',
  'SINH', 'SQRT', 'TAN', 'TANH', 'TRUNC',
  // Date / time
  'DATE', 'TIME', 'DATETIME', 'JULIANDAY', 'STRFTIME', 'TIMEDIFF',
  'UNIXEPOCH',
  // JSON (3.38+)
  'JSON', 'JSON_ARRAY', 'JSON_ARRAY_LENGTH', 'JSON_EACH', 'JSON_ERROR_POSITION',
  'JSON_EXTRACT', 'JSON_INSERT', 'JSON_OBJECT', 'JSON_PATCH', 'JSON_PRETTY',
  'JSON_QUOTE', 'JSON_REMOVE', 'JSON_REPLACE', 'JSON_SET', 'JSON_TREE',
  'JSON_TYPE', 'JSON_VALID', 'JSON_GROUP_ARRAY', 'JSON_GROUP_OBJECT',
  // JSONB (3.45+)
  'JSONB', 'JSONB_ARRAY', 'JSONB_EXTRACT', 'JSONB_INSERT', 'JSONB_OBJECT',
  'JSONB_PATCH', 'JSONB_REMOVE', 'JSONB_REPLACE', 'JSONB_SET',
  'JSONB_GROUP_ARRAY', 'JSONB_GROUP_OBJECT',
  // Window
  'CUME_DIST', 'DENSE_RANK', 'FIRST_VALUE', 'LAG', 'LAST_VALUE', 'LEAD',
  'NTH_VALUE', 'NTILE', 'PERCENT_RANK', 'RANK', 'ROW_NUMBER',
];

export const SQLITE_BUILTIN_FUNCTIONS: ReadonlySet<string> = new Set(
  SQLITE_BUILTIN_FUNCTION_LIST.map((name) => name.toLowerCase()),
);

/** Lazy lookup; case-insensitive match against the curated set. */
export function isSqliteBuiltinFunction(name: string): boolean {
  return SQLITE_BUILTIN_FUNCTIONS.has(name.toLowerCase());
}

/**
 * The full curated list (UPPER per SQLite manual convention). Resolver
 * consumers dedupe against the user's schema routines (always empty in
 * SQLite) before emission.
 */
export function getSqliteBuiltinFunctions(): readonly string[] {
  return SQLITE_BUILTIN_FUNCTION_LIST;
}
